import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  BellIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

export interface PortalNavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  badge?: string | number;
  match?: (pathname: string) => boolean;
}

export interface PortalNavGroup {
  title: string;
  items: PortalNavItem[];
}

export interface PortalShellProps {
  brand: {
    name: string;
    logoUrl?: string | null;
    /** Used if no logo provided */
    initial?: string;
  };
  user: {
    name?: string;
    email?: string;
    subtitle?: string;
    avatarUrl?: string | null;
  };
  groups: PortalNavGroup[];
  onLogout: () => void;
  onSettings?: () => void;
  /** Optional notification tray renderer; receives open/close state */
  renderNotificationPanel?: (args: { open: boolean; onClose: () => void }) => React.ReactNode;
  /** Optional footer item slots (e.g. help) rendered above profile */
  extraFooter?: React.ReactNode;
  children: React.ReactNode;
}

function classNames(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

function isLinkActive(item: PortalNavItem, pathname: string): boolean {
  if (item.match) return item.match(pathname);
  if (pathname === item.href || pathname === item.href + '/') return true;
  return pathname.startsWith(item.href + '/');
}

export function PortalShell({
  brand,
  user,
  groups,
  onLogout,
  onSettings,
  renderNotificationPanel,
  extraFooter,
  children,
}: PortalShellProps) {
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const displayName = user.name || user.email || 'User';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="app-shell">
      <div className="app-container">
        <aside className="sidebar">
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <div className="sidebar-logo-icon">
                {brand.logoUrl ? (
                  <img src={brand.logoUrl} alt={brand.name} className="sidebar-logo-img" />
                ) : (
                  <span className="portal-brand-initial">
                    {brand.initial || brand.name.slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <span className="sidebar-logo-text">{brand.name}</span>
            </div>

            {renderNotificationPanel && (
              <div className="sidebar-header-actions">
                <button
                  type="button"
                  onClick={() => setIsNotificationOpen((o) => !o)}
                  className="sidebar-header-btn"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <BellIcon className="w-5 h-5" aria-hidden />
                </button>
                {renderNotificationPanel({
                  open: isNotificationOpen,
                  onClose: () => setIsNotificationOpen(false),
                })}
              </div>
            )}
          </div>

          <nav className="sidebar-nav">
            {groups.map((group) => (
              <div key={group.title} className="nav-group">
                <div className="nav-group-header">
                  <span className="nav-group-title">{group.title}</span>
                </div>
                <div className="nav-group-items">
                  {group.items.map((item) => {
                    const active = isLinkActive(item, location.pathname);
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        title={item.name}
                        className={classNames('nav-item', active && 'active')}
                      >
                        <Icon aria-hidden className="w-5 h-5" />
                        <span>{item.name}</span>
                        {item.badge && <span className="nav-badge">{item.badge}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="sidebar-footer">
            {extraFooter}
            <div className="sidebar-profile sidebar-profile-card">
              <button
                type="button"
                className="sidebar-profile-trigger"
                onClick={() => setIsProfileOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={isProfileOpen}
              >
                <div className="sidebar-profile-avatar" style={{ padding: 0, overflow: 'hidden' }}>
                  {user.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
                <div className="sidebar-profile-info">
                  <span className="sidebar-profile-name">{displayName}</span>
                  <span className="sidebar-profile-role">{user.subtitle || user.email || ''}</span>
                </div>
              </button>
              {isProfileOpen && (
                <div className="sidebar-profile-dropdown" role="menu">
                  {onSettings && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileOpen(false);
                        onSettings();
                      }}
                      className="sidebar-profile-menu-item"
                      role="menuitem"
                    >
                      <Cog6ToothIcon className="w-4 h-4" aria-hidden />
                      <span>Settings</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onLogout}
                    className="sidebar-profile-menu-item sidebar-profile-logout"
                    role="menuitem"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" aria-hidden />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>

        <div className="main-content">{children}</div>
      </div>
    </div>
  );
}

export default PortalShell;
